import {
  EReviewSubmissionStatus,
  type TError,
  type TNotifyReviewSubmission,
  type TRejectReviewSubmission,
  type TReviewPerson,
  type TSubmitFormAnswer,
} from '@onesource/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import axios from '../../axios';
import type { TReviewAnswer, TReviewSubmissionDetail } from './types';
import { getReviewQueryKey } from './use-review-queue';

const getReviewSubmissionQueryKey = (spaceId: string, submissionId: string) => [
  ...getReviewQueryKey(spaceId),
  'submission',
  submissionId,
];

/** A student's submission with the Feedback form and feedback so far. */
export const useGetReviewSubmission = (spaceId: string, submissionId: string) =>
  useQuery<TReviewSubmissionDetail, TError>({
    queryKey: getReviewSubmissionQueryKey(spaceId, submissionId),
    queryFn: async () => {
      const res = await axios.get<TReviewSubmissionDetail>(
        `/community-hub/spaces/${spaceId}/review/submissions/${submissionId}`,
      );
      return res.data;
    },
    enabled: !!spaceId && !!submissionId,
  });

/** A just-saved answer in the shape the review endpoints return, for patching the cache. */
const toReviewAnswer = (answer: TSubmitFormAnswer, submissionId: string): TReviewAnswer => ({
  // The answer row id is not returned by the save; the editor reads answers by question id only.
  id: answer.questionId,
  submissionId,
  questionId: answer.questionId,
  selectedOptionIds: answer.selectedOptionIds ?? [],
  orderingSequence: answer.orderingSequence ?? [],
  textAnswer: answer.textAnswer ?? null,
  fileUrls: answer.fileUrls ?? [],
  value: answer.value ?? null,
});

interface FeedbackInput {
  spaceId: string;
  submissionId: string;
  answers: TSubmitFormAnswer[];
}

/**
 * Autosave the teacher's feedback before it is delivered. The first save turns the
 * submission into a draft, so the page chip and the queue counts are updated then.
 */
export const useSaveReviewDraft = () => {
  const queryClient = useQueryClient();
  return useMutation<{ feedbackSubmissionId: string }, TError, FeedbackInput>({
    mutationFn: async ({ spaceId, submissionId, answers }) => {
      const res = await axios.put<{ feedbackSubmissionId: string }>(
        `/community-hub/spaces/${spaceId}/review/submissions/${submissionId}/feedback/draft`,
        { answers },
      );
      return res.data;
    },
    onSuccess: ({ feedbackSubmissionId }, { spaceId, submissionId, answers }) => {
      const detailKey = getReviewSubmissionQueryKey(spaceId, submissionId);
      const detail = queryClient.getQueryData<TReviewSubmissionDetail>(detailKey);
      if (!detail) {
        return;
      }
      const isFirstSave = !detail.feedback;
      // Patch the open page instead of refetching it, so the editor keeps what the teacher types.
      // The saved answers go in too: when the teacher comes back to this submission, the editor
      // starts from this cached copy, and an empty copy would lose the draft.
      queryClient.setQueryData<TReviewSubmissionDetail>(detailKey, {
        ...detail,
        status: EReviewSubmissionStatus.in_progress,
        feedback: {
          submissionId: feedbackSubmissionId,
          answers: answers.map(answer => toReviewAnswer(answer, feedbackSubmissionId)),
          submittedAt: null,
          updatedAt: null,
        },
      });
      if (isFirstSave) {
        queryClient.invalidateQueries({ queryKey: [...getReviewQueryKey(spaceId), 'queue'] });
      }
    },
  });
};

/** Reject a submission with a reason; the student is notified and may submit again. */
export const useRejectReviewSubmission = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, TError, TRejectReviewSubmission & { spaceId: string; submissionId: string }>({
    mutationFn: async ({ spaceId, submissionId, reason }) => {
      const res = await axios.post(`/community-hub/spaces/${spaceId}/review/submissions/${submissionId}/reject`, {
        reason,
      });
      return res.data;
    },
    onSuccess: (_result, { spaceId }) => {
      queryClient.invalidateQueries({ queryKey: getReviewQueryKey(spaceId) });
    },
  });
};

/** Admin: send a note about a submission to its teacher or its student (in-app + email). */
export const useNotifyReviewSubmission = () => {
  const queryClient = useQueryClient();
  return useMutation<
    { sentTo: TReviewPerson },
    TError,
    TNotifyReviewSubmission & { spaceId: string; submissionId: string }
  >({
    mutationFn: async ({ spaceId, submissionId, recipient, note }) => {
      const res = await axios.post<{ sentTo: TReviewPerson }>(
        `/community-hub/spaces/${spaceId}/review/submissions/${submissionId}/notify`,
        { recipient, note },
      );
      return res.data;
    },
    // The review panel lists the notes, so it reloads to show the new one.
    onSuccess: (_result, { spaceId }) => {
      queryClient.invalidateQueries({ queryKey: getReviewQueryKey(spaceId) });
    },
  });
};

/** Deliver feedback once; it is locked after this and the student is notified. */
export const useSubmitReviewFeedback = () => {
  const queryClient = useQueryClient();
  return useMutation<{ feedbackSubmissionId: string }, TError, FeedbackInput>({
    mutationFn: async ({ spaceId, submissionId, answers }) => {
      const res = await axios.post<{ feedbackSubmissionId: string }>(
        `/community-hub/spaces/${spaceId}/review/submissions/${submissionId}/feedback`,
        { answers },
      );
      return res.data;
    },
    onSuccess: (_result, { spaceId }) => {
      queryClient.invalidateQueries({ queryKey: getReviewQueryKey(spaceId) });
    },
  });
};
